import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "mb-install-dismissed-at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h — re-ask the next day.
const isAdminRoute = () =>
  typeof window !== "undefined" && window.location.pathname.startsWith("/masti-backdoor");
const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);
const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

// Sticky top "Download App" banner. Uses the PWA install prompt on Android /
// Chrome / Edge; falls back to iOS instructions and a graceful hint on unknown
// platforms. Auto-hides when the app is already installed, in admin routes,
// or the user dismissed it in the last 24h.
export const AppInstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isAdminRoute() || isStandalone()) return;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_TTL_MS) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => setVisible(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // If the browser never fires beforeinstallprompt (Safari/iOS), we still
    // want to show the banner with iOS-specific instructions.
    if (isIOS()) {
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIosHint(false);
  };

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") {
          setVisible(false);
        }
      } catch {
        /* user closed */
      }
      setDeferred(null);
    } else if (isIOS()) {
      setShowIosHint((v) => !v);
    }
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 28 }}
        data-testid="install-banner"
        className="sticky top-0 z-40 w-full bg-gradient-to-r from-[#022C22] via-[#064E3B] to-[#022C22] text-white shadow-[0_6px_16px_rgba(0,0,0,0.15)]"
      >
        <div className="max-w-6xl mx-auto flex items-center gap-3 px-3 sm:px-6 py-2.5">
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <span className="font-display text-lg text-[#E0B53C]">M</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">
              Download the MastiBazaar app
            </p>
            <p className="text-[11px] text-white/70 leading-tight truncate">
              Faster shopping, order tracking, offline browsing.
            </p>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            data-testid="install-banner-btn"
            className="shrink-0 inline-flex items-center gap-1.5 bg-[#E0B53C] text-[#022C22] rounded-full px-3.5 py-1.5 text-xs font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <Download size={12} strokeWidth={2.4} />
            Download
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="install-banner-close"
            aria-label="dismiss"
            className="shrink-0 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70"
          >
            <X size={13} strokeWidth={2} />
          </button>
        </div>
        {showIosHint && (
          <div
            data-testid="install-banner-ios-hint"
            className="px-3 sm:px-6 pb-2 -mt-1 text-[11px] text-white/80"
          >
            On iPhone / iPad: tap the <strong>Share</strong> button in Safari,
            then choose <strong>Add to Home Screen</strong>.
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
